import React, { useEffect, useState } from 'react';
import { 
  BarChart3, Users, Mail, TrendingUp, Globe, FileText, 
  MessageSquare, MousePointer, Eye, UserMinus, RefreshCw,
  ArrowUpRight, ArrowDownRight, Languages, Newspaper, Clock, Link2,
  Monitor, Smartphone, Tablet, Chrome, ArrowRightLeft, UserPlus, X, ExternalLink,
  Search, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import api from '../utils/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';

// Language code to name mapping
const LANGUAGE_NAMES = {
  en: 'English', fr: 'French', de: 'German', es: 'Spanish', it: 'Italian',
  pt: 'Portuguese', nl: 'Dutch', pl: 'Polish', da: 'Danish', sv: 'Swedish',
  no: 'Norwegian', fi: 'Finnish', ru: 'Russian', uk: 'Ukrainian', cs: 'Czech',
  el: 'Greek', tr: 'Turkish', ar: 'Arabic', zh: 'Chinese', ja: 'Japanese',
  ko: 'Korean', hi: 'Hindi', ro: 'Romanian', hu: 'Hungarian', bg: 'Bulgarian'
};

const StatCard = ({ icon: Icon, label, value, subValue, trend, color = 'indigo' }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between">
      <div className={`w-12 h-12 rounded-lg bg-${color}-100 flex items-center justify-center`}>
        <Icon className={`text-${color}-600`} size={24} />
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 text-sm ${trend >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
          {trend >= 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
          {Math.abs(trend)}%
        </div>
      )}
    </div>
    <p className="text-3xl font-bold mt-4">{value?.toLocaleString() || 0}</p>
    <p className="text-slate-600 text-sm">{label}</p>
    {subValue && <p className="text-slate-400 text-xs mt-1">{subValue}</p>}
  </div>
);

const MiniBarChart = ({ data, height = 100 }) => {
  if (!data || data.length === 0) return null;
  const maxValue = Math.max(...data.map(d => d.value), 1);
  
  return (
    <div className="flex items-end gap-1" style={{ height }}>
      {data.slice(-30).map((d, i) => (
        <div
          key={i}
          className="flex-1 bg-indigo-500 rounded-t hover:bg-indigo-600 transition-colors cursor-pointer group relative"
          style={{ height: `${(d.value / maxValue) * 100}%`, minHeight: d.value > 0 ? 4 : 0 }}
          title={`${d.label}: ${d.value}`}
        >
          <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-10">
            {d.label}: {d.value}
          </div>
        </div>
      ))}
    </div>
  );
};

const ProgressBar = ({ label, value, total, color = 'indigo' }) => {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-slate-600">{label}</span>
        <span className="font-medium">{value.toLocaleString()}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div 
          className={`h-full bg-${color}-500 rounded-full transition-all duration-500`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    </div>
  );
};

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [overview, setOverview] = useState(null);
  const [subscribers, setSubscribers] = useState(null);
  const [newsletters, setNewsletters] = useState(null);
  const [translation, setTranslation] = useState(null);
  const [contentAnalytics, setContentAnalytics] = useState(null);
  const [trafficAnalytics, setTrafficAnalytics] = useState(null);
  const [subscriberDays, setSubscriberDays] = useState('30');
  const [contentDays, setContentDays] = useState('30');
  const [trafficDays, setTrafficDays] = useState('30');
  
  // Page detail analytics
  const [selectedPage, setSelectedPage] = useState(null);
  const [pageAnalytics, setPageAnalytics] = useState(null);
  const [pageAnalyticsLoading, setPageAnalyticsLoading] = useState(false);
  const [showPageDetail, setShowPageDetail] = useState(false);
  const [pageDetailDays, setPageDetailDays] = useState('30');
  // Full list of pages + articles powering the "drill into any content"
  // combobox above the Top Pages / Top Articles cards. Loaded once on
  // mount because most tenants have < 500 pages+articles and the search
  // needs to be instant. If a tenant ever exceeds ~2000 items, switch
  // this to a server-side search endpoint.
  const [allPages, setAllPages] = useState([]);
  const [allArticles, setAllArticles] = useState([]);
  const [contentPickerOpen, setContentPickerOpen] = useState(false);

  useEffect(() => {
    fetchAllAnalytics();
    // Also load the searchable page + article catalogue used by the
    // Content Performance drill-down picker. Silent on failure — the
    // picker just stays empty rather than blocking the whole dashboard.
    (async () => {
      try {
        const [pagesRes, articlesRes] = await Promise.all([
          api.get('/pages').catch(() => ({ data: [] })),
          api.get('/articles').catch(() => ({ data: [] })),
        ]);
        setAllPages(Array.isArray(pagesRes.data) ? pagesRes.data : []);
        // /articles endpoint returns { articles: [...] } in some tenants
        // and a bare array in others — handle both shapes.
        const articles = Array.isArray(articlesRes.data)
          ? articlesRes.data
          : (articlesRes.data?.articles || []);
        setAllArticles(articles);
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    fetchSubscriberAnalytics();
  }, [subscriberDays]);

  useEffect(() => {
    fetchContentAnalytics();
  }, [contentDays]);

  useEffect(() => {
    fetchTrafficAnalytics();
  }, [trafficDays]);

  useEffect(() => {
    if (selectedPage) {
      fetchPageAnalytics(selectedPage.page_id, selectedPage.type);
    }
  }, [selectedPage, pageDetailDays]);

  const fetchAllAnalytics = async () => {
    setLoading(true);
    try {
      const [overviewRes, subscribersRes, newslettersRes, translationRes, contentRes, trafficRes] = await Promise.all([
        api.get('/analytics/overview'),
        api.get(`/analytics/subscribers?days=${subscriberDays}`),
        api.get('/analytics/newsletters'),
        api.get('/analytics/translation'),
        api.get(`/analytics/content?days=${contentDays}`),
        api.get(`/analytics/traffic?days=${trafficDays}`)
      ]);
      setOverview(overviewRes.data);
      setSubscribers(subscribersRes.data);
      setNewsletters(newslettersRes.data);
      setTranslation(translationRes.data);
      setContentAnalytics(contentRes.data);
      setTrafficAnalytics(trafficRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriberAnalytics = async () => {
    try {
      const res = await api.get(`/analytics/subscribers?days=${subscriberDays}`);
      setSubscribers(res.data);
    } catch (error) {
      console.error('Failed to fetch subscriber analytics:', error);
    }
  };

  const fetchContentAnalytics = async () => {
    try {
      const res = await api.get(`/analytics/content?days=${contentDays}`);
      setContentAnalytics(res.data);
    } catch (error) {
      console.error('Failed to fetch content analytics:', error);
    }
  };

  const fetchTrafficAnalytics = async () => {
    try {
      const res = await api.get(`/analytics/traffic?days=${trafficDays}`);
      setTrafficAnalytics(res.data);
    } catch (error) {
      console.error('Failed to fetch traffic analytics:', error);
    }
  };

  const fetchPageAnalytics = async (id, type) => {
    setPageAnalyticsLoading(true);
    try {
      const endpoint = type === 'article' 
        ? `/analytics/article/${id}?days=${pageDetailDays}`
        : `/analytics/page/${id}?days=${pageDetailDays}`;
      const res = await api.get(endpoint);
      setPageAnalytics(res.data);
    } catch (error) {
      console.error('Failed to fetch page analytics:', error);
      toast.error('Failed to load page analytics');
    } finally {
      setPageAnalyticsLoading(false);
    }
  };

  const openPageDetail = (page, type = 'page') => {
    setSelectedPage({ ...page, type, page_id: page.page_id || page.article_id });
    setShowPageDetail(true);
  };

  const closePageDetail = () => {
    setShowPageDetail(false);
    setSelectedPage(null);
    setPageAnalytics(null);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllAnalytics();
    setRefreshing(false);
    toast.success('Analytics refreshed');
  };

  // Prepare chart data
  const subscriberChartData = subscribers?.growth?.map(g => ({
    label: g.date,
    value: g.new
  })) || [];

  const totalSubscribers = subscribers?.total_active || 0;
  const totalBySource = Object.values(subscribers?.by_source || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="flex" data-testid="analytics-page">
      <Sidebar />
      
      <div className="flex-1 min-w-0 bg-slate-50 p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Analytics
              </h1>
              <p className="text-slate-600">Track your content performance and audience growth</p>
            </div>
            <Button 
              variant="outline" 
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="refresh-analytics"
            >
              <RefreshCw size={18} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-20">
              <RefreshCw size={40} className="animate-spin mx-auto text-indigo-600 mb-4" />
              <p className="text-slate-500">Loading analytics...</p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Overview Stats */}
              <section>
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <BarChart3 size={20} className="text-indigo-600" />
                  Overview
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard 
                    icon={FileText} 
                    label="Total Pages" 
                    value={overview?.content?.pages} 
                    color="indigo"
                  />
                  <StatCard 
                    icon={Mail} 
                    label="Newsletters Sent" 
                    value={overview?.email?.total_sent}
                    subValue={`${overview?.content?.newsletters || 0} campaigns`}
                    color="emerald"
                  />
                  <StatCard 
                    icon={Users} 
                    label="Active Subscribers" 
                    value={overview?.subscribers?.total}
                    color="blue"
                  />
                  <StatCard 
                    icon={MessageSquare} 
                    label="Form Submissions" 
                    value={overview?.subscribers?.form_submissions}
                    subValue={`${overview?.content?.forms || 0} forms`}
                    color="purple"
                  />
                </div>
              </section>

              {/* Email Performance */}
              <section>
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <Mail size={20} className="text-emerald-600" />
                  Email Performance
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <StatCard 
                    icon={Eye} 
                    label="Open Rate" 
                    value={`${overview?.email?.open_rate || 0}%`}
                    subValue={`${overview?.email?.total_opens?.toLocaleString() || 0} total opens`}
                    color="emerald"
                  />
                  <StatCard 
                    icon={MousePointer} 
                    label="Click Rate" 
                    value={`${overview?.email?.click_rate || 0}%`}
                    subValue={`${overview?.email?.total_clicks?.toLocaleString() || 0} total clicks`}
                    color="blue"
                  />
                  <StatCard 
                    icon={TrendingUp} 
                    label="Bounce Rate" 
                    value={`${overview?.email?.bounce_rate || 0}%`}
                    subValue={`${overview?.email?.total_bounces?.toLocaleString() || 0} bounced`}
                    color="amber"
                  />
                  <StatCard 
                    icon={UserMinus} 
                    label="Unsubscribes" 
                    value={overview?.email?.total_unsubscribes || 0}
                    color="red"
                  />
                </div>
              </section>

              {/* Subscriber Growth */}
              <section>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold flex items-center gap-2">
                    <Users size={20} className="text-blue-600" />
                    Subscriber Growth
                  </h2>
                  <Select value={subscriberDays} onValueChange={setSubscriberDays}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">Last 7 days</SelectItem>
                      <SelectItem value="30">Last 30 days</SelectItem>
                      <SelectItem value="90">Last 90 days</SelectItem>
                      <SelectItem value="365">Last year</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Growth Chart */}
                  <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6">
                    <h3 className="font-medium mb-4">New Subscribers</h3>
                    <MiniBarChart data={subscriberChartData} height={150} />
                    <div className="flex justify-between text-xs text-slate-400 mt-2">
                      <span>{subscribers?.growth?.[0]?.date || ''}</span>
                      <span>{subscribers?.growth?.[subscribers.growth.length - 1]?.date || ''}</span>
                    </div>
                  </div>
                  
                  {/* By Source */}
                  <div className="bg-white border border-slate-200 rounded-xl p-6">
                    <h3 className="font-medium mb-4">By Source</h3>
                    {Object.entries(subscribers?.by_source || {}).slice(0, 5).map(([source, count]) => (
                      <ProgressBar 
                        key={source} 
                        label={source === 'csv_import' ? 'CSV Import' : source === 'form' ? 'Form Signup' : source || 'Direct'} 
                        value={count} 
                        total={totalBySource}
                        color="blue"
                      />
                    ))}
                  </div>
                </div>
                
                {/* Subscriber Groups */}
                {subscribers?.groups?.length > 0 && (
                  <div className="bg-white border border-slate-200 rounded-xl p-6 mt-4">
                    <h3 className="font-medium mb-4">Subscriber Groups</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {subscribers.groups.map(group => (
                        <div key={group.group_id} className="bg-slate-50 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-indigo-600">{group.subscriber_count || 0}</p>
                          <p className="text-sm text-slate-600 truncate" title={group.name}>{group.name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* By Language */}
                {Object.keys(subscribers?.by_language || {}).length > 1 && (
                  <div className="bg-white border border-slate-200 rounded-xl p-6 mt-4">
                    <h3 className="font-medium mb-4 flex items-center gap-2">
                      <Globe size={18} className="text-slate-500" />
                      Subscribers by Language
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {Object.entries(subscribers.by_language)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 12)
                        .map(([lang, count]) => (
                        <div key={lang} className="bg-slate-50 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-blue-600">{count}</p>
                          <p className="text-sm text-slate-600">{LANGUAGE_NAMES[lang] || lang.toUpperCase()}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>

              {/* Newsletter Campaigns */}
              <section>
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <Mail size={20} className="text-emerald-600" />
                  Recent Campaigns
                </h2>
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">Newsletter</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-slate-500 uppercase">Sent</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-slate-500 uppercase">Opens</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-slate-500 uppercase">Clicks</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-slate-500 uppercase">Open Rate</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-slate-500 uppercase">Click Rate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {newsletters?.campaigns?.slice(0, 10).map((campaign) => (
                        <tr key={campaign.campaign_id} className="hover:bg-slate-50">
                          <td className="px-6 py-4">
                            <p className="font-medium text-slate-800">{campaign.title}</p>
                            <p className="text-sm text-slate-500">{campaign.sent_at?.split('T')[0]}</p>
                          </td>
                          <td className="text-center px-4 py-4 font-medium">{campaign.sent_count}</td>
                          <td className="text-center px-4 py-4">{campaign.opened_count}</td>
                          <td className="text-center px-4 py-4">{campaign.clicked_count}</td>
                          <td className="text-center px-4 py-4">
                            <span className={`inline-flex px-2 py-1 rounded-full text-sm font-medium ${
                              campaign.open_rate >= 30 ? 'bg-emerald-100 text-emerald-700' :
                              campaign.open_rate >= 20 ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {campaign.open_rate}%
                            </span>
                          </td>
                          <td className="text-center px-4 py-4">
                            <span className={`inline-flex px-2 py-1 rounded-full text-sm font-medium ${
                              campaign.click_rate >= 5 ? 'bg-emerald-100 text-emerald-700' :
                              campaign.click_rate >= 2 ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {campaign.click_rate}%
                            </span>
                          </td>
                        </tr>
                      ))}
                      {(!newsletters?.campaigns || newsletters.campaigns.length === 0) && (
                        <tr>
                          <td colSpan={6} className="text-center py-8 text-slate-500">
                            No campaigns sent yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                {newsletters?.averages && newsletters.campaigns?.length > 0 && (
                  <div className="mt-4 p-4 bg-indigo-50 rounded-lg">
                    <p className="text-sm text-indigo-800">
                      <strong>Averages across {newsletters.total_campaigns} campaigns:</strong> {newsletters.averages.open_rate}% open rate, {newsletters.averages.click_rate}% click rate
                    </p>
                  </div>
                )}
              </section>

              {/* Translation Analytics */}
              {translation?.enabled && (
                <section>
                  <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <Languages size={20} className="text-purple-600" />
                    Translation Usage
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <StatCard 
                      icon={Globe} 
                      label="Cached Translations" 
                      value={translation?.cache?.total_cached || 0}
                      color="purple"
                    />
                    <StatCard 
                      icon={FileText} 
                      label="Characters Translated" 
                      value={(translation?.cache?.total_characters || 0).toLocaleString()}
                      color="indigo"
                    />
                    <StatCard 
                      icon={TrendingUp} 
                      label="Estimated Cost" 
                      value={`$${translation?.cache?.estimated_cost || 0}`}
                      subValue="Based on Google Translate pricing"
                      color="emerald"
                    />
                  </div>
                  
                  {translation?.by_language?.length > 0 && (
                    <div className="bg-white border border-slate-200 rounded-xl p-6 mt-4">
                      <h3 className="font-medium mb-4">Translations by Language</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                        {translation.by_language.slice(0, 12).map(lang => (
                          <div key={lang.language} className="bg-slate-50 rounded-lg p-3 text-center">
                            <p className="text-xl font-bold text-purple-600">{lang.translations}</p>
                            <p className="text-sm text-slate-600">{LANGUAGE_NAMES[lang.language] || lang.language?.toUpperCase()}</p>
                            <p className="text-xs text-slate-400">{(lang.characters / 1000).toFixed(1)}k chars</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              )}

              {/* Content Performance */}
              <section>
                <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                  <h2 className="text-xl font-semibold flex items-center gap-2">
                    <Eye size={20} className="text-cyan-600" />
                    Content Performance
                  </h2>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Searchable drill-down into ANY page or article
                        (not just the Top-10 lists below). Clicking an
                        item opens the same detail modal used by the Top
                        Pages / Top Articles rows. */}
                    <Popover open={contentPickerOpen} onOpenChange={setContentPickerOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="h-9 gap-2 text-sm"
                          data-testid="content-drilldown-trigger"
                          aria-expanded={contentPickerOpen}
                        >
                          <Search size={14} />
                          Drill into any page or article
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0 w-[380px]" align="end">
                        <Command
                          filter={(value, search) => {
                            // Value we set on each CommandItem is
                            // "<title> <slug> <type>", so a substring
                            // match against the lowercased combined
                            // string is enough for title/slug search.
                            return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
                          }}
                        >
                          <CommandInput
                            placeholder="Search all pages & articles…"
                            data-testid="content-drilldown-search"
                          />
                          <CommandList>
                            <CommandEmpty>No matching content.</CommandEmpty>
                            {allPages.length > 0 && (
                              <CommandGroup heading="Pages">
                                {allPages.map((p) => (
                                  <CommandItem
                                    key={`page-${p.page_id}`}
                                    value={`${p.title || ''} ${p.slug || ''} page`}
                                    onSelect={() => {
                                      setContentPickerOpen(false);
                                      openPageDetail(
                                        { page_id: p.page_id, title: p.title, slug: p.slug },
                                        'page'
                                      );
                                    }}
                                    data-testid={`content-drilldown-page-${p.page_id}`}
                                  >
                                    <FileText size={14} className="text-cyan-600 mr-2 shrink-0" />
                                    <div className="min-w-0 flex-1">
                                      <div className="truncate text-sm">{p.title || '(untitled)'}</div>
                                      <div className="truncate text-xs text-slate-400">/{p.slug}</div>
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            )}
                            {allArticles.length > 0 && (
                              <CommandGroup heading="Articles">
                                {allArticles.map((a) => (
                                  <CommandItem
                                    key={`article-${a.article_id}`}
                                    value={`${a.title || ''} ${a.slug || ''} article`}
                                    onSelect={() => {
                                      setContentPickerOpen(false);
                                      openPageDetail(
                                        { article_id: a.article_id, title: a.title, slug: a.slug },
                                        'article'
                                      );
                                    }}
                                    data-testid={`content-drilldown-article-${a.article_id}`}
                                  >
                                    <Newspaper size={14} className="text-amber-600 mr-2 shrink-0" />
                                    <div className="min-w-0 flex-1">
                                      <div className="truncate text-sm">{a.title || '(untitled)'}</div>
                                      {a.slug && <div className="truncate text-xs text-slate-400">/{a.slug}</div>}
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            )}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <Select value={contentDays} onValueChange={setContentDays}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7">Last 7 days</SelectItem>
                        <SelectItem value="30">Last 30 days</SelectItem>
                        <SelectItem value="90">Last 90 days</SelectItem>
                        <SelectItem value="365">Last year</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Summary Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <StatCard 
                    icon={Eye} 
                    label="Total Page Views" 
                    value={contentAnalytics?.summary?.total_views || 0}
                    color="cyan"
                  />
                  <StatCard 
                    icon={Users} 
                    label="Unique Visitors" 
                    value={contentAnalytics?.summary?.unique_visitors || 0}
                    color="blue"
                  />
                  <StatCard 
                    icon={TrendingUp} 
                    label="Avg Views/Day" 
                    value={contentAnalytics?.summary?.avg_views_per_day || 0}
                    color="emerald"
                  />
                </div>
                
                {/* Views Over Time Chart */}
                {contentAnalytics?.views_over_time?.length > 0 && (
                  <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
                    <h3 className="font-medium mb-4">Page Views Over Time</h3>
                    <MiniBarChart 
                      data={contentAnalytics.views_over_time.map(d => ({
                        label: d.date,
                        value: d.views
                      }))} 
                      height={150} 
                    />
                    <div className="flex justify-between text-xs text-slate-400 mt-2">
                      <span>{contentAnalytics.views_over_time[0]?.date || ''}</span>
                      <span>{contentAnalytics.views_over_time[contentAnalytics.views_over_time.length - 1]?.date || ''}</span>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Top Pages */}
                  <div className="bg-white border border-slate-200 rounded-xl p-6">
                    <h3 className="font-medium mb-4 flex items-center gap-2">
                      <FileText size={18} className="text-slate-500" />
                      Top Pages
                      <span className="text-xs text-slate-400 ml-auto">Click for details</span>
                    </h3>
                    {contentAnalytics?.top_pages?.length > 0 ? (
                      <div className="space-y-3">
                        {contentAnalytics.top_pages.map((page, idx) => (
                          <div 
                            key={page.page_id || idx} 
                            className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50 rounded-lg px-2 -mx-2 transition-colors"
                            onClick={() => openPageDetail(page, 'page')}
                          >
                            <div className="flex items-center gap-3">
                              <span className="w-6 h-6 bg-cyan-100 text-cyan-700 rounded-full flex items-center justify-center text-sm font-medium">
                                {idx + 1}
                              </span>
                              <div>
                                <p className="font-medium text-slate-800 truncate max-w-[200px]" title={page.title}>
                                  {page.title}
                                </p>
                                <p className="text-xs text-slate-400">/{page.slug}</p>
                              </div>
                            </div>
                            <div className="text-right flex items-center gap-3">
                              <div>
                                <p className="font-semibold text-slate-700">{page.views}</p>
                                <p className="text-xs text-slate-400">{page.unique_visitors} unique</p>
                              </div>
                              <ExternalLink size={14} className="text-slate-400" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-400 text-sm py-8 text-center">No page view data yet</p>
                    )}
                  </div>
                  
                  {/* Top Articles */}
                  <div className="bg-white border border-slate-200 rounded-xl p-6">
                    <h3 className="font-medium mb-4 flex items-center gap-2">
                      <Newspaper size={18} className="text-slate-500" />
                      Top Articles
                      <span className="text-xs text-slate-400 ml-auto">Click for details</span>
                    </h3>
                    {contentAnalytics?.top_articles?.length > 0 ? (
                      <div className="space-y-3">
                        {contentAnalytics.top_articles.map((article, idx) => (
                          <div 
                            key={article.article_id || idx} 
                            className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50 rounded-lg px-2 -mx-2 transition-colors"
                            onClick={() => openPageDetail(article, 'article')}
                          >
                            <div className="flex items-center gap-3">
                              <span className="w-6 h-6 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-sm font-medium">
                                {idx + 1}
                              </span>
                              <p className="font-medium text-slate-800 truncate max-w-[200px]" title={article.title}>
                                {article.title}
                              </p>
                            </div>
                            <div className="text-right flex items-center gap-3">
                              <div>
                                <p className="font-semibold text-slate-700">{article.views}</p>
                                <p className="text-xs text-slate-400">{article.unique_visitors} unique</p>
                              </div>
                              <ExternalLink size={14} className="text-slate-400" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-400 text-sm py-8 text-center">No article view data yet</p>
                    )}
                  </div>
                </div>
                
                {/* Views by Hour & Referrers */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                  {/* Views by Hour */}
                  {contentAnalytics?.views_by_hour && Object.keys(contentAnalytics.views_by_hour).length > 0 && (
                    <div className="bg-white border border-slate-200 rounded-xl p-6">
                      <h3 className="font-medium mb-4 flex items-center gap-2">
                        <Clock size={18} className="text-slate-500" />
                        Views by Hour (UTC)
                      </h3>
                      <div className="flex items-end gap-1" style={{ height: 80 }}>
                        {Array.from({ length: 24 }, (_, i) => {
                          const views = contentAnalytics.views_by_hour[i] || 0;
                          const maxHour = Math.max(...Object.values(contentAnalytics.views_by_hour), 1);
                          return (
                            <div
                              key={i}
                              className="flex-1 bg-cyan-400 rounded-t hover:bg-cyan-500 transition-colors cursor-pointer"
                              style={{ height: `${(views / maxHour) * 100}%`, minHeight: views > 0 ? 4 : 0 }}
                              title={`${i}:00 - ${views} views`}
                            />
                          );
                        })}
                      </div>
                      <div className="flex justify-between text-xs text-slate-400 mt-2">
                        <span>12am</span>
                        <span>6am</span>
                        <span>12pm</span>
                        <span>6pm</span>
                        <span>12am</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Top Referrers */}
                  {contentAnalytics?.top_referrers?.length > 0 && (
                    <div className="bg-white border border-slate-200 rounded-xl p-6">
                      <h3 className="font-medium mb-4 flex items-center gap-2">
                        <Link2 size={18} className="text-slate-500" />
                        Top Referrers
                      </h3>
                      <div className="space-y-2">
                        {contentAnalytics.top_referrers.slice(0, 5).map((ref, idx) => {
                          const totalReferrers = contentAnalytics.top_referrers.reduce((a, r) => a + r.views, 0);
                          const percentage = totalReferrers > 0 ? (ref.views / totalReferrers) * 100 : 0;
                          return (
                            <div key={idx}>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-slate-600 truncate max-w-[200px]" title={ref.referrer}>
                                  {ref.referrer}
                                </span>
                                <span className="font-medium">{ref.views}</span>
                              </div>
                              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-indigo-500 rounded-full transition-all duration-500"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* No data message */}
                {(!contentAnalytics?.top_pages?.length && !contentAnalytics?.top_articles?.length) && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                    <p className="text-sm text-blue-800">
                      <strong>No page view data yet.</strong> Page views are tracked automatically when visitors view your public site pages and articles.
                    </p>
                  </div>
                )}
              </section>

              {/* Website Traffic */}
              <section>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold flex items-center gap-2">
                    <Monitor size={20} className="text-violet-600" />
                    Website Traffic
                  </h2>
                  <Select value={trafficDays} onValueChange={setTrafficDays}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">Last 7 days</SelectItem>
                      <SelectItem value="30">Last 30 days</SelectItem>
                      <SelectItem value="90">Last 90 days</SelectItem>
                      <SelectItem value="365">Last year</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Engagement Stats */}
                {trafficAnalytics?.engagement && (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    <StatCard 
                      icon={Users} 
                      label="Sessions" 
                      value={trafficAnalytics.engagement.total_sessions}
                      color="violet"
                    />
                    <StatCard 
                      icon={FileText} 
                      label="Pages/Session" 
                      value={trafficAnalytics.engagement.pages_per_session}
                      color="blue"
                    />
                    <StatCard 
                      icon={ArrowRightLeft} 
                      label="Bounce Rate" 
                      value={`${trafficAnalytics.engagement.bounce_rate}%`}
                      color="amber"
                    />
                    <StatCard 
                      icon={UserPlus} 
                      label="New Visitors" 
                      value={trafficAnalytics.engagement.new_visitors}
                      color="emerald"
                    />
                    <StatCard 
                      icon={Users} 
                      label="Returning" 
                      value={trafficAnalytics.engagement.returning_visitors}
                      color="indigo"
                    />
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Devices */}
                  {trafficAnalytics?.devices?.length > 0 && (
                    <div className="bg-white border border-slate-200 rounded-xl p-6">
                      <h3 className="font-medium mb-4 flex items-center gap-2">
                        <Monitor size={18} className="text-slate-500" />
                        Devices
                      </h3>
                      <div className="space-y-3">
                        {trafficAnalytics.devices.map((d, idx) => {
                          const total = trafficAnalytics.devices.reduce((a, x) => a + x.views, 0);
                          const percentage = total > 0 ? (d.views / total) * 100 : 0;
                          const DeviceIcon = d.device === 'mobile' ? Smartphone : d.device === 'tablet' ? Tablet : Monitor;
                          return (
                            <div key={idx}>
                              <div className="flex items-center justify-between text-sm mb-1">
                                <span className="flex items-center gap-2 text-slate-600 capitalize">
                                  <DeviceIcon size={14} />
                                  {d.device}
                                </span>
                                <span className="font-medium">{d.views} ({percentage.toFixed(0)}%)</span>
                              </div>
                              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-violet-500 rounded-full"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Browsers */}
                  {trafficAnalytics?.browsers?.length > 0 && (
                    <div className="bg-white border border-slate-200 rounded-xl p-6">
                      <h3 className="font-medium mb-4 flex items-center gap-2">
                        <Chrome size={18} className="text-slate-500" />
                        Browsers
                      </h3>
                      <div className="space-y-3">
                        {trafficAnalytics.browsers.slice(0, 5).map((b, idx) => {
                          const total = trafficAnalytics.browsers.reduce((a, x) => a + x.views, 0);
                          const percentage = total > 0 ? (b.views / total) * 100 : 0;
                          return (
                            <div key={idx}>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-slate-600">{b.browser}</span>
                                <span className="font-medium">{b.views} ({percentage.toFixed(0)}%)</span>
                              </div>
                              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-blue-500 rounded-full"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Operating Systems */}
                  {trafficAnalytics?.operating_systems?.length > 0 && (
                    <div className="bg-white border border-slate-200 rounded-xl p-6">
                      <h3 className="font-medium mb-4 flex items-center gap-2">
                        <Monitor size={18} className="text-slate-500" />
                        Operating Systems
                      </h3>
                      <div className="space-y-3">
                        {trafficAnalytics.operating_systems.slice(0, 5).map((o, idx) => {
                          const total = trafficAnalytics.operating_systems.reduce((a, x) => a + x.views, 0);
                          const percentage = total > 0 ? (o.views / total) * 100 : 0;
                          return (
                            <div key={idx}>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-slate-600">{o.os}</span>
                                <span className="font-medium">{o.views} ({percentage.toFixed(0)}%)</span>
                              </div>
                              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-emerald-500 rounded-full"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                  {/* Traffic Sources */}
                  {trafficAnalytics?.traffic_sources?.length > 0 && (
                    <div className="bg-white border border-slate-200 rounded-xl p-6">
                      <h3 className="font-medium mb-4 flex items-center gap-2">
                        <TrendingUp size={18} className="text-slate-500" />
                        Traffic Sources
                      </h3>
                      <div className="space-y-3">
                        {trafficAnalytics.traffic_sources.map((s, idx) => {
                          const total = trafficAnalytics.traffic_sources.reduce((a, x) => a + x.views, 0);
                          const percentage = total > 0 ? (s.views / total) * 100 : 0;
                          const sourceColors = {
                            direct: 'bg-slate-500',
                            search: 'bg-emerald-500',
                            social: 'bg-pink-500',
                            referral: 'bg-indigo-500',
                            email: 'bg-amber-500'
                          };
                          return (
                            <div key={idx}>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-slate-600 capitalize">{s.source}</span>
                                <span className="font-medium">{s.views} ({percentage.toFixed(0)}%)</span>
                              </div>
                              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full rounded-full ${sourceColors[s.source] || 'bg-slate-400'}`}
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Top Referring Domains */}
                  {trafficAnalytics?.top_domains?.length > 0 && (
                    <div className="bg-white border border-slate-200 rounded-xl p-6">
                      <h3 className="font-medium mb-4 flex items-center gap-2">
                        <Link2 size={18} className="text-slate-500" />
                        Top Referring Sites
                      </h3>
                      <div className="space-y-2">
                        {trafficAnalytics.top_domains.slice(0, 8).map((d, idx) => (
                          <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                            <span className="text-sm text-slate-600 truncate max-w-[200px]" title={d.domain}>
                              {d.domain}
                            </span>
                            <span className="text-sm font-medium text-slate-700">{d.views}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Visitor Languages */}
                {trafficAnalytics?.visitor_languages?.length > 0 && (
                  <div className="bg-white border border-slate-200 rounded-xl p-6 mt-6">
                    <h3 className="font-medium mb-4 flex items-center gap-2">
                      <Globe size={18} className="text-slate-500" />
                      Visitor Languages
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {trafficAnalytics.visitor_languages.slice(0, 12).map((l, idx) => (
                        <div key={idx} className="bg-slate-50 rounded-lg p-3 text-center">
                          <p className="text-xl font-bold text-violet-600">{l.views}</p>
                          <p className="text-sm text-slate-600">{LANGUAGE_NAMES[l.language?.split('-')[0]] || l.language}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* No traffic data message */}
                {(!trafficAnalytics?.devices?.length) && (
                  <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 mt-4">
                    <p className="text-sm text-violet-800">
                      <strong>No traffic data yet.</strong> Traffic data is collected automatically when visitors view your public site.
                    </p>
                  </div>
                )}
              </section>
            </div>
          )}
        </div>
      </div>

      {/* Page Detail Modal */}
      <Dialog open={showPageDetail} onOpenChange={(open) => !open && closePageDetail()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {selectedPage?.type === 'article' ? (
                  <Newspaper size={24} className="text-amber-600" />
                ) : (
                  <FileText size={24} className="text-cyan-600" />
                )}
                <div>
                  <h2 className="text-xl font-bold">{pageAnalytics?.page?.title || pageAnalytics?.article?.title || 'Loading...'}</h2>
                  <p className="text-sm text-slate-500 font-normal">
                    /{pageAnalytics?.page?.slug || pageAnalytics?.article?.slug}
                  </p>
                </div>
              </div>
              <Select value={pageDetailDays} onValueChange={setPageDetailDays}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="365">Last year</SelectItem>
                </SelectContent>
              </Select>
            </DialogTitle>
          </DialogHeader>
          
          {pageAnalyticsLoading ? (
            <div className="py-12 text-center">
              <RefreshCw size={32} className="animate-spin mx-auto text-indigo-600 mb-4" />
              <p className="text-slate-500">Loading analytics...</p>
            </div>
          ) : pageAnalytics ? (
            <div className="space-y-6 pt-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-cyan-600">{pageAnalytics.summary?.views || 0}</p>
                  <p className="text-sm text-slate-600">Page Views</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">{pageAnalytics.summary?.unique_visitors || 0}</p>
                  <p className="text-sm text-slate-600">Unique Visitors</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-emerald-600">{pageAnalytics.summary?.avg_views_per_day || 0}</p>
                  <p className="text-sm text-slate-600">Avg/Day</p>
                </div>
              </div>
              
              {/* Views Over Time */}
              {pageAnalytics.views_over_time?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <h4 className="font-medium mb-3">Views Over Time</h4>
                  <MiniBarChart 
                    data={pageAnalytics.views_over_time.map(d => ({ label: d.date, value: d.views }))} 
                    height={120} 
                  />
                  <div className="flex justify-between text-xs text-slate-400 mt-2">
                    <span>{pageAnalytics.views_over_time[0]?.date}</span>
                    <span>{pageAnalytics.views_over_time[pageAnalytics.views_over_time.length - 1]?.date}</span>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                {/* Devices */}
                {pageAnalytics.devices?.length > 0 && (
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Monitor size={16} className="text-slate-500" />
                      Devices
                    </h4>
                    <div className="space-y-2">
                      {pageAnalytics.devices.map((d, idx) => {
                        const total = pageAnalytics.devices.reduce((a, x) => a + x.views, 0);
                        const pct = total > 0 ? (d.views / total) * 100 : 0;
                        return (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="capitalize text-slate-600">{d.device}</span>
                            <span className="font-medium">{d.views} ({pct.toFixed(0)}%)</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Browsers */}
                {pageAnalytics.browsers?.length > 0 && (
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Chrome size={16} className="text-slate-500" />
                      Browsers
                    </h4>
                    <div className="space-y-2">
                      {pageAnalytics.browsers.slice(0, 5).map((b, idx) => {
                        const total = pageAnalytics.browsers.reduce((a, x) => a + x.views, 0);
                        const pct = total > 0 ? (b.views / total) * 100 : 0;
                        return (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-slate-600">{b.browser}</span>
                            <span className="font-medium">{b.views} ({pct.toFixed(0)}%)</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Traffic Sources */}
                {pageAnalytics.traffic_sources?.length > 0 && (
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <TrendingUp size={16} className="text-slate-500" />
                      Traffic Sources
                    </h4>
                    <div className="space-y-2">
                      {pageAnalytics.traffic_sources.map((s, idx) => {
                        const total = pageAnalytics.traffic_sources.reduce((a, x) => a + x.views, 0);
                        const pct = total > 0 ? (s.views / total) * 100 : 0;
                        return (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="capitalize text-slate-600">{s.source}</span>
                            <span className="font-medium">{s.views} ({pct.toFixed(0)}%)</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Top Referrers */}
                {pageAnalytics.top_referrers?.length > 0 && (
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Link2 size={16} className="text-slate-500" />
                      Top Referrers
                    </h4>
                    <div className="space-y-2">
                      {pageAnalytics.top_referrers.slice(0, 5).map((r, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-slate-600 truncate max-w-[150px]" title={r.domain}>{r.domain}</span>
                          <span className="font-medium">{r.views}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Views by Hour */}
              {pageAnalytics.views_by_hour && Object.keys(pageAnalytics.views_by_hour).length > 0 && (
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Clock size={16} className="text-slate-500" />
                    Views by Hour (UTC)
                  </h4>
                  <div className="flex items-end gap-1" style={{ height: 60 }}>
                    {Array.from({ length: 24 }, (_, i) => {
                      const views = pageAnalytics.views_by_hour[i] || 0;
                      const maxHour = Math.max(...Object.values(pageAnalytics.views_by_hour), 1);
                      return (
                        <div
                          key={i}
                          className="flex-1 bg-cyan-400 rounded-t hover:bg-cyan-500 transition-colors"
                          style={{ height: `${(views / maxHour) * 100}%`, minHeight: views > 0 ? 4 : 0 }}
                          title={`${i}:00 - ${views} views`}
                        />
                      );
                    })}
                  </div>
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>12am</span>
                    <span>6am</span>
                    <span>12pm</span>
                    <span>6pm</span>
                    <span>12am</span>
                  </div>
                </div>
              )}
              
              {/* No data */}
              {!pageAnalytics.views_over_time?.length && (
                <div className="bg-slate-50 rounded-lg p-6 text-center">
                  <Eye size={32} className="mx-auto text-slate-300 mb-2" />
                  <p className="text-slate-500">No view data for this period</p>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Analytics;
